import { Component, OnInit, HostListener, NgZone } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, Subscription, takeUntil } from "rxjs";
import { EntityType } from "src/app/enums/entity-type.enum";
import { FragmentDto, FragmentLineDto } from "src/app/models/fragment-dto";
import { Entity } from "src/app/models/mvp-query-response-dto";
import { ApiService } from "src/app/services/api.service";
import { LanguageService } from "src/app/services/language.service";
import { RoutingService } from "src/app/services/routing.service";
import { ScreenDimensionsService } from "src/app/services/screen-dimensions.service";
import { ImageInput } from "../iiif-viewer/iiif-tiled-viewer.component";
import { GaService } from "src/app/services/ga.service";

@Component({
  selector: 'fragment',
  templateUrl: './fragment.component.html',
  styleUrls: ['./fragment.component.scss'],
})
export class FragmentComponent implements OnInit {
    private entityDetailsSubscription?: Subscription;
    closeClicked: Subject<void> = new Subject<void>();
    fragmentDetails: FragmentDto | null = null;
    currentLanguage$!: Observable<string>;
    isSmallScreen = false;
    entity: Entity = {} as Entity;
    entityId: number = -1;
    entityType: string = EntityType.FRAGMENT;
    fragmentDetailsLoading: boolean = false;
    viewerImages: ImageInput[] = [];
    fragmentLines:FragmentLineDto[] = [];
    currentLanguage!:string;

    constructor(
        private screenDimensionsService: ScreenDimensionsService,
        private routingService: RoutingService,
        private apiService: ApiService,
        private languageService: LanguageService,
        private gaService: GaService,
        private ngZone: NgZone
    ) { }
    
    ngOnInit(): void {
        const currentLanguage = this.languageService.currentLanguage.getValue();
        this.currentLanguage$ = this.languageService.currentLanguage$;
        this.screenDimensionsService.isSmallScreen$.subscribe(isSmallScreen => {
          this.isSmallScreen = isSmallScreen;
        });
        this.entityDetailsSubscription = this.routingService.entityDetails$.subscribe(entityDetails => {
          this.ngZone.run(() => {
            const { id, type } = entityDetails;
            if (!!id && type === this.entityType) {
              this.entityId = id;
              this.getFragmentDetails();
            }
          });
        });
    }
    
    public getFragmentDetails(): void {
        this.fragmentDetailsLoading = true;
        this.apiService
            .getFragmentDetails(this.entityId)
            .pipe(takeUntil(this.closeClicked))
            .subscribe({
                next: (res: FragmentDto) => {
                    this.fragmentDetails = res;
                    this.prepareViewerImages();
                    this.fragmentLines = res.fragmentLines || [];
                    this.fragmentDetailsLoading = false;
                },
                error: () => {
                    this.fragmentDetailsLoading = false;
                },
                complete: () => {
                    this.fragmentDetailsLoading = false;
                }
            });
    }
    
    private prepareViewerImages(): void {
        if (this.fragmentDetails && this.fragmentDetails.fragmentImages) {
            this.viewerImages = this.fragmentDetails.fragmentImages.map(image => ({
                id: image.imageType.toLowerCase().replace(/\s+/g, ''),
                name: this.formatImageTypeName(image.imageType),
                baseUrl: image.imageUrl
            }));
        }
    }
    
    private formatImageTypeName(imageType: string): string {
        return imageType
            .split(/[\s_-]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    goToManuscript() {
        this.gaService.trackWithGA(EntityType.MANUSCRIPT, 'entityType')
        const queryParams = {
            "entityId": this.fragmentDetails?.manuscriptEntityId,
            "entityType": EntityType.MANUSCRIPT
        }
        this.routingService.navigateWithMerge(queryParams, ['']);
    }

    @HostListener('window:resize', ['$event'])
    onWindowResize(event: any) {
        if (this.fragmentDetails) {
            this.prepareViewerImages();
        }
    }
}